import React, { useState, useEffect } from 'react'
import { format } from 'date-fns'
import { pl } from 'date-fns/locale'
import { useNotificationContext } from '../../contexts/NotificationContext'
import ServiceCreator from './ServiceCreator'
import ServiceNotes, { ServiceNote } from './ServiceNotes'

interface Client { id: number; name: string }

interface ServiceRow {
  id: string
  client_id: number
  client_name: string
  date: string
  service_type: string
  status: string
  priority: string
  street_address: string
  location?: string
  related_issue_id?: string
  author_name?: string
  planned_date?: string
  created_at?: string
}

interface ServiceDetail extends ServiceRow {
  latitude?: string
  longitude?: string
  // Ślad realizacji: kto zlecił (author_name), kto zaplanował, kto zrealizował.
  planned_by_name?: string | null
  completed_by_name?: string | null
  infrastructure_object_id?: number | null
  infrastructure_object?: {
    id: number
    name?: string | null
    address?: string | null
    location?: string | null
    street_address?: string | null
  } | null
  description?: string
  additional_tools?: string
  completion_comment?: string
  additional_materials?: string
  created_at?: string
  images?: Array<{
    id: number
    filename: string
    original_filename: string
    file_path: string
    file_size: number
    mime_type: string
    description?: string
    uploaded_at: string
  }>
  notes?: ServiceNote[]
  can_add_note?: boolean
  status_history?: Array<{
    id: number
    old_status?: string
    new_status: string
    changed_by_name: string
    changed_at: string
    planning_date?: string
    completion_comment?: string
  }>
}

interface User {
  id: number
  username: string
  email: string
  full_name: string
  role: string
  is_active: boolean
  created_at: string
  last_services_interaction?: string
}

interface ServicesListProps { token: string }

export default function ServicesList({ token }: ServicesListProps) {
  const [services, setServices] = useState<ServiceRow[]>([])
  const [clients, setClients] = useState<Client[]>([])
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showCreator, setShowCreator] = useState(false)
  
  // Modal planowania
  const [showPlanModal, setShowPlanModal] = useState(false)
  const [planningServiceId, setPlanningServiceId] = useState<string>('')
  const [plannedDate, setPlannedDate] = useState('')

  // Modal szczegółów
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [selectedServiceDetail, setSelectedServiceDetail] = useState<ServiceDetail | null>(null)
  const [loadingDetail, setLoadingDetail] = useState(false)

  // Modal edycji
  const [editingServiceId, setEditingServiceId] = useState<string | null>(null)

  // Modal zakończenia
  const [showCompletionModal, setShowCompletionModal] = useState(false)
  const [completionServiceId, setCompletionServiceId] = useState<string>('')
  const [completionComment, setCompletionComment] = useState('')
  const [additionalMaterials, setAdditionalMaterials] = useState('')
  const [completionPhotos, setCompletionPhotos] = useState<File[]>([])
  const [generateReport, setGenerateReport] = useState(true)

  // Obsługa gestów dotykowych dla mobilnych
  const [touchStart, setTouchStart] = useState<number | null>(null)
  const [touchEnd, setTouchEnd] = useState<number | null>(null)

  // Filtry
  const [clientFilterInput, setClientFilterInput] = useState<number | ''>('')
  const [dateFromFilterInput, setDateFromFilterInput] = useState('')
  const [dateToFilterInput, setDateToFilterInput] = useState('')
  const [serviceTypeFilterInput, setServiceTypeFilterInput] = useState<string>('')
  const [statusFilterInput, setStatusFilterInput] = useState<string>('')
  const [priorityFilterInput, setPriorityFilterInput] = useState<string>('')

  const [clientFilter, setClientFilter] = useState<number | ''>('')
  const [dateFromFilter, setDateFromFilter] = useState('')
  const [dateToFilter, setDateToFilter] = useState('')
  const [serviceTypeFilter, setServiceTypeFilter] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [prioritFilter, setPriorityFilter] = useState<string>('')

  // Sortowanie
  const [sortBy, setSortBy] = useState('date')
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC')

  const { showSuccess, showError, showConfirm } = useNotificationContext()

  // Uprawnienia muszą odpowiadać temu, co egzekwuje backend
  // (can_plan_service / can_complete_service w app/core/permissions.py):
  // planowanie należy do kierownika, realizacja do technika. Bez tego technik
  // widziałby przycisk "Zaplanuj" i dostawał 403 dopiero po kliknięciu.
  const roleUpper = (user?.role || '').toUpperCase()
  const canCreateService = ['URZEDNIK', 'KIEROWNIK', 'ADMIN', 'ADMINISTRATOR', 'MASTER_ADMIN'].includes(roleUpper)
  // Edycja zlecenia: URZEDNIK i wyżej, niezależnie od autorstwa i statusu —
  // musi odpowiadać can_edit_services w app/core/permissions.py.
  const canEditService = ['URZEDNIK', 'KIEROWNIK', 'ADMIN', 'ADMINISTRATOR', 'MASTER_ADMIN'].includes(roleUpper)
  const canPlanService = ['KIEROWNIK', 'ADMIN', 'ADMINISTRATOR', 'MASTER_ADMIN'].includes(roleUpper)
  const canCompleteService = ['USER', 'URZEDNIK', 'KIEROWNIK', 'ADMIN', 'ADMINISTRATOR', 'MASTER_ADMIN'].includes(roleUpper)

  useEffect(() => {
    const loadUserData = async () => {
      try {
        const response = await fetch('/api/auth/me', {
          headers: { 'Authorization': `Bearer ${token}` },
        })
        if (response.ok) {
          const userData = await response.json()
          setUser(userData)
        }
      } catch (err) {
        console.error('Error loading user data:', err)
      }
    }

    loadUserData()
    loadClients()
    loadServices()
  }, [clientFilter, dateFromFilter, dateToFilter, serviceTypeFilter, statusFilter, prioritFilter, sortBy, sortOrder])

  // Wejście ze zgłoszenia (?createFromIssue=...) otwiera kreator zlecenia.
  // Czekamy na wczytanie roli: przy pierwszym renderze `user` jest jeszcze null,
  // więc warunek w poprzednim efekcie albo blokowałby urzędnika, albo otwierał
  // kreator technikowi — który i tak dostałby 403 dopiero przy zapisie.
  useEffect(() => {
    if (!user || showCreator) return
    const createFromIssue = new URLSearchParams(window.location.search).get('createFromIssue')
    if (createFromIssue && canCreateService) {
      setShowCreator(true)
    }
  }, [user])

  // Handle openModal query parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const openModal = urlParams.get('openModal')

    if (openModal && !showDetailsModal) {
      openDetailsModal(openModal)
    }
  }, [services]) // Wait for services to load first

  const handleFilterClick = () => {
    setClientFilter(clientFilterInput)
    setDateFromFilter(dateFromFilterInput)
    setDateToFilter(dateToFilterInput)
    setServiceTypeFilter(serviceTypeFilterInput)
    setStatusFilter(statusFilterInput)
    setPriorityFilter(priorityFilterInput)
  }

  const clearInputFilters = () => {
    setClientFilterInput('')
    setDateFromFilterInput('')
    setDateToFilterInput('')
    setServiceTypeFilterInput('')
    setStatusFilterInput('')
    setPriorityFilterInput('')
  }

  const clearActiveFilters = () => {
    setClientFilter('')
    setDateFromFilter('')
    setDateToFilter('')
    setServiceTypeFilter('')
    setStatusFilter('')
    setPriorityFilter('')
  }

  const clearFilters = () => {
    clearInputFilters()
    clearActiveFilters()
  }

  const loadClients = async () => {
    try {
      const response = await fetch('/api/clients/list', {
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (response.ok) {
        const data = await response.json()
        setClients(data)
      }
    } catch (err) {
      console.error('Error loading clients:', err)
    }
  }

  const loadServices = async () => {
    try {
      setLoading(true)
      setError('')
      const params = new URLSearchParams()
      if (clientFilter) params.append('client_id', clientFilter.toString())
      if (dateFromFilter) params.append('date_from', dateFromFilter)
      if (dateToFilter) params.append('date_to', dateToFilter)
      if (serviceTypeFilter) params.append('service_type', serviceTypeFilter)
      if (statusFilter) params.append('status', statusFilter)
      if (prioritFilter) params.append('priority', prioritFilter)
      params.append('sort_by', sortBy)
      params.append('sort_order', sortOrder)

      const response = await fetch(`/api/services?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      })

      if (!response.ok) {
        setError('Błąd podczas ładowania serwisów')
        setServices([])
        return
      }

      const list: ServiceRow[] = await response.json()
      setServices(list)
    } catch (err) {
      setError('Błąd połączenia z serwerem')
      setServices([])
    } finally {
      setLoading(false)
    }
  }

  const markServicesSeen = async () => {
    try {
      await fetch('/api/services/seen', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      })
    } catch (error) {
      console.error('Error marking services as seen:', error)
    }
  }

  const markServiceAsSeen = async (serviceId: string) => {
    try {
      await fetch(`/api/services/${serviceId}/seen`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      })
    } catch (error) {
      console.error('Error marking service as seen:', error)
    }
  }

  const updateStatus = async (serviceId: string, action: 'plan' | 'complete', plannedDate?: string) => {
    try {
      // Build URL with query parameters
      let url = `/api/services/${serviceId}/status?action=${action}`
      if (action === 'plan' && plannedDate) {
        url += `&planned_date=${plannedDate}`
      }

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({}),
      })
      if (response.ok) {
        const data = await response.json()
        showSuccess('Sukces', data.message)
        loadServices()
      } else {
        const errorData = await response.json()
        showError('Błąd aktualizacji statusu', errorData.detail || 'Błąd podczas aktualizacji statusu')
      }
    } catch {
      showError('Błąd połączenia', 'Nie można połączyć się z serwerem')
    }
  }

  const openPlanModal = (serviceId: string) => {
    // Ustaw domyślną datę na jutrzejszy dzień
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    const year = tomorrow.getFullYear()
    const month = String(tomorrow.getMonth() + 1).padStart(2, '0')
    const day = String(tomorrow.getDate()).padStart(2, '0')
    setPlannedDate(`${year}-${month}-${day}`)
    setPlanningServiceId(serviceId)
    setShowPlanModal(true)
  }

  const confirmPlan = async () => {
    if (!plannedDate) {
      showError('Błąd', 'Wybierz datę planowania')
      return
    }

    await updateStatus(planningServiceId, 'plan', plannedDate)
    setShowPlanModal(false)
    setPlanningServiceId('')
    setPlannedDate('')
  }

  const openDetailsModal = async (serviceId: string) => {
    setLoadingDetail(true)
    setShowDetailsModal(true)
    try {
      const response = await fetch(`/api/services/${serviceId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (response.ok) {
        const data = await response.json()
        setSelectedServiceDetail(data)

        // Mark this specific service as seen
        await markServiceAsSeen(serviceId)
      } else {
        showError('Błąd', 'Nie udało się pobrać szczegółów zlecenia')
        setShowDetailsModal(false)
      }
    } catch (err) {
      showError('Błąd połączenia', 'Nie można połączyć się z serwerem')
      setShowDetailsModal(false)
    } finally {
      setLoadingDetail(false)
    }
  }

  const openCompletionModal = (serviceId: string) => {
    setCompletionServiceId(serviceId)
    setCompletionComment('')
    setAdditionalMaterials('')
    setCompletionPhotos([])
    setGenerateReport(true) // Domyślnie zaznaczony
    setShowCompletionModal(true)
  }

  const completeService = async () => {
    console.log('FRONTEND: completeService called')
    console.log('FRONTEND: completionComment:', completionComment)
    console.log('FRONTEND: additionalMaterials:', additionalMaterials)
    console.log('FRONTEND: generateReport:', generateReport)
    console.log('FRONTEND: completionServiceId:', completionServiceId)

    if (!completionComment.trim()) {
      showError('Błąd', 'Opis wykonanych prac jest wymagany')
      return
    }

    // Upload zdjęć zakończenia pracy jeśli zostały wybrane
    let uploadedPhotoIds: string[] = []
    if (completionPhotos.length > 0) {
      console.log('FRONTEND: Uploading photos:', completionPhotos.length)
      try {
        for (const photo of completionPhotos) {
          const formData = new FormData()
          formData.append('file', photo)
          formData.append('description', `Zdjęcie zakończenia pracy - ${completionComment.substring(0, 50)}...`)

          const response = await fetch(`/api/services/${completionServiceId}/images`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
            },
            body: formData,
          })

          if (response.ok) {
            const data = await response.json()
            uploadedPhotoIds.push(data.id.toString())
            console.log('FRONTEND: Photo uploaded successfully:', data.id)
          } else {
            console.error('FRONTEND: Error uploading photo:', await response.text())
            // Nie przerywamy procesu zakończenia zlecenia z powodu błędu uploadu zdjęcia
          }
        }
      } catch (error) {
        console.error('FRONTEND: Error uploading photos:', error)
        // Nie przerywamy procesu zakończenia zlecenia z powodu błędu uploadu zdjęć
      }
    }

    const requestPayload = {
      completion_comment: completionComment,
      additional_materials: additionalMaterials || null,
      generate_report: generateReport
    }

    console.log('FRONTEND: Sending request payload:', requestPayload)

    try {
      const response = await fetch(`/api/services/${completionServiceId}/status?action=complete`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestPayload),
      })

      console.log('FRONTEND: Response status:', response.status)
      console.log('FRONTEND: Response ok:', response.ok)

      if (response.ok) {
        const data = await response.json()
        console.log('FRONTEND: Response data:', data)

        if (generateReport) {
          showSuccess('Sukces', `${data.message} Raport pracy został wygenerowany.`)
        } else {
          showSuccess('Sukces', data.message)
        }

        setShowCompletionModal(false)
        setCompletionServiceId('')
        setCompletionComment('')
        setAdditionalMaterials('')
        setCompletionPhotos([])
        setGenerateReport(true)
        loadServices()
      } else {
        const errorText = await response.text()
        console.log('FRONTEND: Error response text:', errorText)
        let errorData
        try {
          errorData = JSON.parse(errorText)
        } catch (e) {
          errorData = { detail: errorText }
        }
        console.log('FRONTEND: Parsed error data:', errorData)
        showError('Błąd zakończenia', errorData.detail || 'Błąd podczas zakończenia serwisu')
      }
    } catch (error) {
      console.error('FRONTEND: Network error:', error)
      showError('Błąd połączenia', 'Nie można połączyć się z serwerem')
    }
  }

  const deleteService = async (serviceId: string) => {
    const confirmed = await showConfirm('Potwierdzenie usunięcia', `Czy na pewno chcesz usunąć serwis ${serviceId}?`)
    if (!confirmed) return

    try {
      const response = await fetch(`/api/services/${serviceId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (response.ok) {
        showSuccess('Serwis został usunięty pomyślnie')
        loadServices()
      } else {
        const errorData = await response.json()
        showError('Błąd usuwania', errorData.detail || 'Błąd podczas usuwania serwisu')
      }
    } catch {
      showError('Błąd połączenia', 'Nie można połączyć się z serwerem')
    }
  }

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC')
    } else {
      setSortBy(field)
      setSortOrder('DESC')
    }
  }

  const getSortIcon = (field: string) => {
    if (sortBy !== field) return '↕️'
    return sortOrder === 'ASC' ? '↑' : '↓'
  }

  const getServiceTypeLabel = (serviceType: string) => {
    const labels = {
      'odbiór_przyłącza': 'Odbiór przyłącza',
      'częściowy_odbiór_przyłącza': 'Częściowy odbiór przyłącza',
      'wymiana_wodomierza': 'Wymiana wodomierza',
      'usunięcie_awarii': 'Usunięcie awarii',
      'zamknięcie_przyłącza': 'Zamknięcie przyłącza',
      'kontrola_stacji_uzdatniania_wody': 'Kontrola SUW',
      'zlecenie_wewnętrzne': 'Zlecenie wewnętrzne',
      'skoordynacja': 'Koordynacja',
      'inne': 'Inne'
    }
    return labels[serviceType as keyof typeof labels] || serviceType
  }

  const getStatusLabel = (status: string) => {
    const labels = {
      'utworzone': 'Utworzone',
      'zaplanowane': 'Zaplanowane',
      'zrealizowane': 'Zrealizowane'
    }
    return labels[status as keyof typeof labels] || status
  }

  const getPriorityLabel = (priority: string) => {
    const labels = {
      'niski': 'Niski',
      'średni': 'Średni',
      'wysoki': 'Wysoki'
    }
    return labels[priority as keyof typeof labels] || priority
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'wysoki': return 'text-red-600 bg-red-100'
      case 'średni': return 'text-yellow-600 bg-yellow-100'
      case 'niski': return 'text-green-600 bg-green-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  const isServiceUnseen = (service: ServiceRow) => {
    if (!user?.last_services_interaction || !service.created_at) {
      return false
    }

    const lastInteraction = new Date(user.last_services_interaction)
    const serviceCreated = new Date(service.created_at)

    return serviceCreated > lastInteraction
  }

  if (loading && services.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2">Ładowanie serwisów...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Zlecenia prac</h2>
        {canCreateService && (
          <button
            onClick={() => setShowCreator(true)}
            className="w-full sm:w-auto min-h-[44px] bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium"
          >
            + Nowe zlecenie
          </button>
        )}
      </div>

      {/* Filtry */}
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Filtry</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Klient */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Klient</label>
            <select
              value={clientFilterInput}
              onChange={(e) => setClientFilterInput(e.target.value === '' ? '' : parseInt(e.target.value))}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Wszyscy klienci</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>{client.name}</option>
              ))}
            </select>
          </div>

          {/* Data od */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data od</label>
            <input
              type="date"
              value={dateFromFilterInput}
              onChange={(e) => setDateFromFilterInput(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Data do */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data do</label>
            <input
              type="date"
              value={dateToFilterInput}
              onChange={(e) => setDateToFilterInput(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Typ zlecenia */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Typ zlecenia</label>
            <select
              value={serviceTypeFilterInput}
              onChange={(e) => setServiceTypeFilterInput(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Wszystkie</option>
              <option value="odbiór_przyłącza">Odbiór przyłącza</option>
              <option value="częściowy_odbiór_przyłącza">Częściowy odbiór przyłącza</option>
              <option value="wymiana_wodomierza">Wymiana wodomierza</option>
              <option value="usunięcie_awarii">Usunięcie awarii</option>
              <option value="zamknięcie_przyłącza">Zamknięcie przyłącza</option>
              <option value="kontrola_stacji_uzdatniania_wody">Kontrola SUW</option>
              <option value="zlecenie_wewnętrzne">Zlecenie wewnętrzne</option>
              <option value="skoordynacja">Koordynacja</option>
              <option value="inne">Inne</option>
            </select>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={statusFilterInput}
              onChange={(e) => setStatusFilterInput(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Wszystkie</option>
              <option value="utworzone">Utworzone</option>
              <option value="zaplanowane">Zaplanowane</option>
              <option value="zrealizowane">Zrealizowane</option>
            </select>
          </div>

          {/* Priorytet */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Priorytet</label>
            <select
              value={priorityFilterInput}
              onChange={(e) => setPriorityFilterInput(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Wszystkie</option>
              <option value="niski">Niski</option>
              <option value="średni">Średni</option>
              <option value="wysoki">Wysoki</option>
            </select>
          </div>

          {/* Przyciski */}
          <div className="flex items-end gap-2">
            <button
              onClick={handleFilterClick}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium"
            >
              Filtruj
            </button>
            <button
              onClick={clearFilters}
              className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md font-medium"
            >
              Wyczyść filtry
            </button>
          </div>
        </div>
      </div>

      {/* Błąd */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      {/* Lista serwisów - desktop: tabela, mobile: karty */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {/* === WIDOK DESKTOP (≥ lg) === */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                  onClick={() => handleSort('date')}
                >
                  Data {getSortIcon('date')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Numer
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Typ
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Adres
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Priorytet
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                  onClick={() => handleSort('status')}
                >
                  Status {getSortIcon('status')}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Akcje
                </th>
              </tr>
            </thead>

            <tbody className="bg-white divide-y divide-gray-200">
              {services.map((service) => (
                <tr key={service.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {format(new Date(service.date), 'dd.MM.yyyy', { locale: pl })}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-blue-500">
                    <button
                      onClick={() => openDetailsModal(service.id)}
                      className={`hover:underline ${isServiceUnseen(service) ? 'font-bold' : ''}`}
                    >
                      {service.id}
                    </button>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {getServiceTypeLabel(service.service_type)}
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-900">
                    <div className="max-w-xs truncate" title={service.street_address}>
                      {service.street_address}
                    </div>
                    {service.location && (
                      <div className="text-xs text-gray-500 truncate" title={service.location}>
                        {service.location}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {getPriorityLabel(service.priority)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {getStatusLabel(service.status)}
                    {service.planned_date && (
                      <div className="text-xs text-gray-500">
                        Planowany: {format(new Date(service.planned_date), 'dd.MM.yyyy', { locale: pl })}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-4 text-right text-sm font-medium">
                    <div className="inline-flex flex-wrap justify-end gap-1">
                      {service.status === 'utworzone' && canPlanService && (
                        <button
                          onClick={() => openPlanModal(service.id)}
                          className="px-2 py-1 rounded text-xs text-green-600 hover:text-green-900 hover:bg-green-50"
                        >
                          Zaplanuj
                        </button>
                      )}
                      {service.status === 'zaplanowane' && canCompleteService && (
                        <button
                          onClick={() => openCompletionModal(service.id)}
                          className="px-2 py-1 rounded text-xs text-blue-600 hover:text-blue-900 hover:bg-blue-50"
                        >
                          Oznacz jako wykonane
                        </button>
                      )}
                      {user?.role === 'MASTER_ADMIN' && (
                        <button
                          onClick={() => deleteService(service.id)}
                          className="px-2 py-1 rounded text-xs text-red-600 hover:text-red-900 hover:bg-red-50 font-medium"
                          title="Master Admin - usuń niezależnie od statusu"
                        >
                          ⚡ Usuń
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* === WIDOK MOBILE / TABLET (< lg): karty === */}
        <div className="lg:hidden">
          {/* Pasek sortowania */}
          <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200 text-xs text-gray-600">
            <span className="font-medium">Sortuj:</span>
            <button
              onClick={() => handleSort('date')}
              className="px-2 py-1 rounded hover:bg-gray-200"
            >
              Data {getSortIcon('date')}
            </button>
            <button
              onClick={() => handleSort('status')}
              className="px-2 py-1 rounded hover:bg-gray-200"
            >
              Status {getSortIcon('status')}
            </button>
          </div>

          <ul className="divide-y divide-gray-200">
            {services.map((service) => (
              <li key={service.id} className="p-3 sm:p-4 hover:bg-gray-50">
                {/* Nagłówek karty */}
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1 min-w-0 flex items-center flex-wrap gap-x-2 gap-y-1">
                    <button
                      onClick={() => openDetailsModal(service.id)}
                      className={`max-w-full truncate inline-flex items-center px-2 py-1 rounded border-2 border-blue-500 text-sm text-blue-700 font-semibold transition-colors hover:bg-gray-100 ${
                        isServiceUnseen(service) ? 'ring-2 ring-blue-300' : ''
                      }`}
                      title="Kliknij aby zobaczyć szczegóły zlecenia"
                    >
                      #{service.id}
                    </button>
                    <span className="text-sm text-gray-700 whitespace-nowrap">
                      {format(new Date(service.date), 'dd.MM.yyyy', { locale: pl })}
                    </span>
                  </div>
                  <span
                    className={`shrink-0 whitespace-nowrap inline-block px-2 py-0.5 text-xs font-medium rounded-full ${
                      service.status === 'zrealizowane'
                        ? 'bg-green-100 text-green-800'
                        : service.status === 'zaplanowane'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-yellow-100 text-yellow-800'
                    }`}
                  >
                    {getStatusLabel(service.status)}
                  </span>
                </div>

                {/* Dane zlecenia */}
                <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-sm">
                  <dt className="text-gray-500">Typ:</dt>
                  <dd className="text-gray-900 break-words">{getServiceTypeLabel(service.service_type)}</dd>

                  <dt className="text-gray-500">Adres:</dt>
                  <dd className="text-gray-900 break-words">
                    {service.street_address}
                    {service.location && (
                      <div className="text-xs text-gray-500">{service.location}</div>
                    )}
                  </dd>

                  <dt className="text-gray-500">Priorytet:</dt>
                  <dd className="text-gray-900">{getPriorityLabel(service.priority)}</dd>

                  {service.planned_date && (
                    <>
                      <dt className="text-gray-500">Planowany:</dt>
                      <dd className="text-gray-900">
                        {format(new Date(service.planned_date), 'dd.MM.yyyy', { locale: pl })}
                      </dd>
                    </>
                  )}
                </dl>

                {/* Akcje */}
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    onClick={() => openDetailsModal(service.id)}
                    className="px-3 py-1.5 text-xs font-medium rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                  >
                    Szczegóły
                  </button>
                  {service.status === 'utworzone' && canPlanService && (
                    <button
                      onClick={() => openPlanModal(service.id)}
                      className="px-3 py-1.5 text-xs font-medium rounded border border-green-300 bg-green-50 text-green-700 hover:bg-green-100"
                    >
                      Zaplanuj
                    </button>
                  )}
                  {service.status === 'zaplanowane' && canCompleteService && (
                    <button
                      onClick={() => openCompletionModal(service.id)}
                      className="px-3 py-1.5 text-xs font-medium rounded border border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100"
                    >
                      Oznacz jako wykonane
                    </button>
                  )}
                  {user?.role === 'MASTER_ADMIN' && (
                    <button
                      onClick={() => deleteService(service.id)}
                      className="px-3 py-1.5 text-xs font-medium rounded border border-red-300 bg-red-50 text-red-700 hover:bg-red-100"
                      title="Master Admin - usuń niezależnie od statusu"
                    >
                      ⚡ Usuń
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>

        {services.length === 0 && !loading && (
          <div className="text-center py-8 text-gray-500">
            Brak serwisów spełniających kryteria wyszukiwania
          </div>
        )}
      </div>

      {/* Podsumowanie */}
      <div className="bg-blue-50 p-4 rounded-lg">
        <div className="flex justify-between items-center text-sm text-blue-800">
          <span>Znaleziono serwisów: <strong>{services.length}</strong></span>
        </div>
      </div>

      {/* Kreator serwisu */}
      <ServiceCreator
        // isOpen musi reagować TAKŻE na editingServiceId, nie tylko na
        // showCreator. Przycisk „Edytuj zlecenie" ustawiał sam
        // editingServiceId i zamykał modal szczegółów, więc kreator nigdy się
        // nie otwierał (ServiceCreator kończy na `if (!isOpen) return null`) —
        // objaw: modal znika i nic się nie dzieje. Błąd wracał już
        // wcześniej, dlatego warunek jest tu, a nie w obsłudze kliknięcia:
        // każda przyszła ścieżka ustawiająca editingServiceId otworzy kreator
        // sama, bez pamiętania o drugim setterze.
        isOpen={showCreator || editingServiceId !== null}
        onClose={() => {
          setShowCreator(false)
          setEditingServiceId(null)
        }}
        onServiceCreated={loadServices}
        token={token}
        editingServiceId={editingServiceId}
      />

      {/* Modal planowania */}
      {showPlanModal && (
        <div className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/50 sm:items-center sm:p-4">
          <div className="bg-white sm:rounded-lg shadow-2xl w-full sm:max-w-md flex flex-col max-h-full sm:max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b shrink-0">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900">Zaplanuj serwis</h3>
              <button
                onClick={() => setShowPlanModal(false)}
                className="p-1 -mr-1 text-gray-400 hover:text-gray-600"
                aria-label="Zamknij"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Wybierz datę wykonania serwisu
                  </label>
                  <input
                    type="date"
                    value={plannedDate}
                    onChange={(e) => setPlannedDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="text-sm text-gray-600">
                  Serwis zostanie zaplanowany na wybraną datę.
                </div>
              </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 px-4 sm:px-6 py-4 border-t shrink-0">
              <button
                onClick={() => setShowPlanModal(false)}
                className="min-h-[44px] px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Anuluj
              </button>
              <button
                onClick={confirmPlan}
                className="min-h-[44px] px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md"
              >
                Zaplanuj
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal szczegółów */}
      {showDetailsModal && (
        <div className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/50 sm:items-center sm:p-4">
          <div className="bg-white sm:rounded-lg shadow-2xl w-full sm:max-w-3xl lg:max-w-6xl flex flex-col max-h-full sm:max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b shrink-0">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 truncate pr-2">
                Szczegóły zlecenia {selectedServiceDetail ? selectedServiceDetail.id : ''}
              </h3>
              <button
                onClick={() => {
                  setShowDetailsModal(false)
                  setSelectedServiceDetail(null)
                }}
                className="p-1 -mr-1 text-gray-400 hover:text-gray-600 shrink-0"
                aria-label="Zamknij"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6">
              {loadingDetail ? (
                <div className="flex items-center justify-center p-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-2">Ładowanie szczegółów...</span>
                </div>
              ) : selectedServiceDetail ? (
                <div className="space-y-6">
                  {/* Nagłówek z podstawowymi informacjami */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-gray-50 rounded-lg p-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Podstawowe informacje</h4>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Numer zlecenia:</span>
                          <span className="font-medium">{selectedServiceDetail.id}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Typ:</span>
                          <span className="font-medium">{getServiceTypeLabel(selectedServiceDetail.service_type)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Status:</span>
                          <span className="font-medium">{getStatusLabel(selectedServiceDetail.status)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Priorytet:</span>
                          <span className={`px-2 py-1 rounded-full text-sm font-semibold ${getPriorityColor(selectedServiceDetail.priority)}`}>
                            {getPriorityLabel(selectedServiceDetail.priority)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Data utworzenia:</span>
                          <span className="font-medium">
                            {selectedServiceDetail.created_at ? format(new Date(selectedServiceDetail.created_at), 'dd.MM.yyyy HH:mm', { locale: pl }) : 'Nieznana'}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Dane klienta i lokalizacja</h4>
                      <div className="space-y-3">
                        <div>
                          <span className="text-gray-600">Klient:</span>
                          <div className="font-medium">{selectedServiceDetail.client_name}</div>
                        </div>
                        <div>
                          <span className="text-gray-600">Zlecił:</span>
                          <div className="font-medium">{selectedServiceDetail.author_name || 'Nieznany'}</div>
                        </div>
                        <div>
                          <span className="text-gray-600">Zaplanował:</span>
                          <div className="font-medium">{selectedServiceDetail.planned_by_name || '—'}</div>
                        </div>
                        <div>
                          <span className="text-gray-600">Zrealizował:</span>
                          <div className="font-medium">{selectedServiceDetail.completed_by_name || '—'}</div>
                        </div>
                        {selectedServiceDetail.infrastructure_object && (
                          <div>
                            <span className="text-gray-600">Obiekt infrastruktury:</span>
                            <div className="font-medium">
                              {[
                                selectedServiceDetail.infrastructure_object.name,
                                selectedServiceDetail.infrastructure_object.location,
                                selectedServiceDetail.infrastructure_object.street_address,
                              ].filter(Boolean).join(' – ')}
                            </div>
                          </div>
                        )}
                        <div>
                          <span className="text-gray-600">Adres:</span>
                          <div className="font-medium">{selectedServiceDetail.street_address}</div>
                        </div>
                        <div>
                          <span className="text-gray-600">Lokalizacja:</span>
                          <div className="font-medium">{selectedServiceDetail.location || 'Nie podano'}</div>
                        </div>
                        {(selectedServiceDetail.latitude && selectedServiceDetail.longitude) && (
                          <div>
                            <span className="text-gray-600">Współrzędne:</span>
                            <div className="font-medium">{selectedServiceDetail.latitude}, {selectedServiceDetail.longitude}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Szczegóły prac */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {selectedServiceDetail.description && (
                      <div className="bg-gray-50 rounded-lg p-6">
                        <h4 className="text-lg font-semibold text-gray-900 mb-4">Opis prac do wykonania</h4>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedServiceDetail.description}</p>
                      </div>
                    )}

                    {selectedServiceDetail.additional_tools && (
                      <div className="bg-gray-50 rounded-lg p-6">
                        <h4 className="text-lg font-semibold text-gray-900 mb-4">Potrzebne narzędzia</h4>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedServiceDetail.additional_tools}</p>
                      </div>
                    )}
                  </div>

                  {/* Zdjęcia */}
                  {selectedServiceDetail.images && selectedServiceDetail.images.length > 0 && (
                    <div className="bg-gray-50 rounded-lg p-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Zdjęcia</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {selectedServiceDetail.images.map((img) => (
                          <div key={img.id} className="bg-white rounded-lg overflow-hidden shadow">
                            <img
                              src={`/${img.file_path}`}
                              alt={img.original_filename}
                              className="w-full h-48 object-cover"
                            />
                            <div className="p-3">
                              <p className="text-sm font-medium text-gray-900 truncate">{img.original_filename}</p>
                              {img.description && (
                                <p className="text-xs text-gray-600 mt-1">{img.description}</p>
                              )}
                              <p className="text-xs text-gray-500 mt-2">
                                {format(new Date(img.uploaded_at), 'dd.MM.yyyy HH:mm', { locale: pl })}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Notatki z realizacji */}
                  <ServiceNotes
                    token={token}
                    serviceId={selectedServiceDetail.id}
                    notes={selectedServiceDetail.notes ?? []}
                    canAdd={!!selectedServiceDetail.can_add_note}
                    onChange={(notes) =>
                      setSelectedServiceDetail((prev) => (prev ? { ...prev, notes } : prev))
                    }
                  />

                  {/* Historia statusów */}
                  {selectedServiceDetail.status_history && selectedServiceDetail.status_history.length > 0 && (
                    <div className="bg-gray-50 rounded-lg p-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Historia zmian statusu</h4>
                      <div className="space-y-2">
                        {selectedServiceDetail.status_history.map((h) => (
                          <div key={h.id} className="bg-white rounded p-3 shadow-sm">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="text-sm text-gray-900">
                                  {h.old_status ?
                                    `Zmieniono z ${getStatusLabel(h.old_status)} na ${getStatusLabel(h.new_status)}` :
                                    `Utworzono: ${getStatusLabel(h.new_status)}`
                                  }
                                </p>
                                <p className="text-xs text-gray-600 mt-1">
                                  przez {h.changed_by_name} • {format(new Date(h.changed_at), 'dd.MM.yyyy HH:mm', { locale: pl })}
                                </p>
                              </div>
                            </div>
                            {h.planning_date && (
                              <p className="text-xs text-blue-600 mt-1">
                                Zaplanowano na: {format(new Date(h.planning_date), 'dd.MM.yyyy', { locale: pl })}
                              </p>
                            )}
                            {h.completion_comment && (
                              <div className="mt-2 p-2 bg-gray-100 rounded text-xs">
                                <strong>Komentarz do zakończenia:</strong><br />
                                {h.completion_comment}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Powiązane zgłoszenie */}
                  {selectedServiceDetail.related_issue_id && (
                    <div className="bg-yellow-50 rounded-lg p-6">
                      <h4 className="text-lg font-semibold text-yellow-900 mb-2">Powiązane zgłoszenie</h4>
                      <p className="text-yellow-800">Numer zgłoszenia: {selectedServiceDetail.related_issue_id}</p>
                    </div>
                  )}

                  {/* Komentarz zakończenia */}
                  {selectedServiceDetail.completion_comment && (
                    <div className="bg-green-50 rounded-lg p-6">
                      <h4 className="text-lg font-semibold text-green-900 mb-2">Komentarz zakończenia prac</h4>
                      <p className="text-green-800 whitespace-pre-wrap">{selectedServiceDetail.completion_comment}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  Nie udało się załadować szczegółów.
                </div>
              )}
            </div>

            <div className="flex justify-between px-6 py-4 border-t">
              {/* Edycja: urzędnik i wyżej, także cudze i już zaplanowane zlecenia */}
              {canEditService && (
                <button
                  onClick={() => {
                    if (selectedServiceDetail) {
                      setEditingServiceId(selectedServiceDetail.id)
                      setShowDetailsModal(false)
                    }
                  }}
                  className="px-4 py-2 min-h-[44px] text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium"
                >
                  Edytuj zlecenie
                </button>
              )}

              <div className="flex gap-3 ml-auto">
                <button
                  onClick={() => {
                    setShowDetailsModal(false)
                    setSelectedServiceDetail(null)
                  }}
                  className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  Zamknij
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal zakończenia */}
      {showCompletionModal && (
        <div className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/50 sm:items-center sm:p-4">
          <div className="bg-white sm:rounded-lg shadow-xl w-full sm:max-w-2xl max-h-full sm:max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 sm:p-6 border-b shrink-0">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Oznacz zlecenie jako wykonane</h2>
              <button onClick={() => setShowCompletionModal(false)} className="p-1 -mr-1 text-gray-400 hover:text-gray-600" aria-label="Zamknij">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); completeService(); }} className="flex-1 min-h-0 flex flex-col overflow-hidden">
              <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6">
              <div className="space-y-6">
                {/* Opis wykonanych prac */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Opis wykonanych prac *</label>
                  <textarea
                    value={completionComment}
                    onChange={(e) => setCompletionComment(e.target.value)}
                    rows={4}
                    placeholder="Opisz szczegółowo jakie prace zostały wykonane..."
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    required
                  />
                </div>

                {/* Dodatkowe materiały/usługi */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Dodatkowe materiały/usługi</label>
                  <textarea
                    value={additionalMaterials}
                    onChange={(e) => setAdditionalMaterials(e.target.value)}
                    rows={3}
                    placeholder="Lista użytych materiałów, usług dodatkowych itp..."
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  />
                </div>

                {/* Zdjęcia zakończenia pracy */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Zdjęcia zakończenia pracy</label>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={(e) => {
                      if (e.target.files) {
                        setCompletionPhotos(Array.from(e.target.files))
                      }
                    }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {completionPhotos && completionPhotos.length > 0 && (
                    <div className="mt-2 text-sm text-gray-600">
                      Wybrano plików: {completionPhotos.length}
                    </div>
                  )}
                </div>

                {/* Generowanie raportu */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="generateReport"
                    checked={generateReport}
                    onChange={(e) => setGenerateReport(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="generateReport" className="ml-2 text-sm font-medium text-gray-700">
                    Generuj raport pracy
                  </label>
                </div>

                {generateReport && (
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                    <p className="text-sm text-blue-800">
                      <strong>Info:</strong> Po zakończeniu zlecenia zostanie automatycznie wygenerowany raport pracy zawierający wszystkie informacje z zlecenia oraz szczegóły zakończenia.
                    </p>
                  </div>
                )}
              </div>

              </div>

              <div className="shrink-0 flex flex-col-reverse sm:flex-row sm:justify-end gap-3 p-4 sm:p-6 border-t">
                <button
                  type="button"
                  onClick={() => setShowCompletionModal(false)}
                  className="min-h-[44px] px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  className="min-h-[44px] px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md"
                >
                  Oznacz jako wykonane
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
